/**
 * Lazy loader + minimal typings for the Twilio Voice JavaScript SDK (v2).
 *
 * The SDK is loaded on demand from Twilio's CDN the first time voice is actually used (never at app
 * boot), so the softphone adds nothing to the initial bundle and pages that don't call stay lean.
 * It attaches a `Twilio` global exposing `Twilio.Device`; we resolve that. Both the dashboard
 * (agent softphone) and the widget (visitor "call agent") consume this.
 *
 * Only the surface we use is typed here — the SDK's own types aren't installed (the local package
 * manager is offline), and a thin typing keeps the call sites honest without the dependency.
 */

/** Pinned Twilio Voice SDK v2 UMD build. Twilio's own CDN (sdk.twilio.com/js/voice/…) only ever
 *  hosted the legacy client 1.x and returns 403 for the v2 `voice/releases` path, so we load the
 *  published npm package (`@twilio/voice-sdk`) from a public CDN — jsDelivr primary, unpkg fallback.
 *  Both expose the same UMD `window.Twilio.Device`. */
const SDK_URLS = [
  'https://cdn.jsdelivr.net/npm/@twilio/voice-sdk@2.14.0/dist/twilio.min.js',
  'https://unpkg.com/@twilio/voice-sdk@2.14.0/dist/twilio.min.js',
];

export type TwilioCallEvent = 'accept' | 'disconnect' | 'cancel' | 'reject' | 'error' | 'mute';

/** An in-progress or incoming call (Twilio ``Call``). */
export interface TwilioCall {
  on(event: TwilioCallEvent, handler: (arg?: unknown) => void): void;
  accept(): void;
  reject(): void;
  ignore(): void;
  disconnect(): void;
  mute(shouldMute?: boolean): void;
  isMuted(): boolean;
  /** Custom params sent from TwiML (`<Client><Parameter>`), on incoming calls. */
  customParameters?: Map<string, string>;
  parameters?: Record<string, string>;
}

export type TwilioDeviceEvent =
  | 'registered'
  | 'unregistered'
  | 'error'
  | 'incoming'
  | 'tokenWillExpire'
  | 'destroyed';

/** A registered browser endpoint (Twilio ``Device``). */
export interface TwilioDevice {
  register(): Promise<void>;
  unregister(): Promise<void>;
  connect(options?: { params?: Record<string, string> }): Promise<TwilioCall>;
  updateToken(token: string): void;
  destroy(): void;
  on(event: TwilioDeviceEvent, handler: (arg?: unknown) => void): void;
}

export interface TwilioDeviceConstructor {
  new (token: string, options?: Record<string, unknown>): TwilioDevice;
}

interface TwilioGlobal {
  Device: TwilioDeviceConstructor;
}

let loadPromise: Promise<TwilioGlobal> | null = null;

function currentGlobal(): TwilioGlobal | null {
  const t = (globalThis as { Twilio?: Partial<TwilioGlobal> }).Twilio;
  return t && typeof t.Device === 'function' ? (t as TwilioGlobal) : null;
}

/**
 * Load the Twilio Voice SDK once (idempotent) and resolve its `{ Device }`. Rejects if the network
 * blocks the CDN or the global never appears — callers should surface a "calling unavailable" state.
 */
/** Inject one <script> and resolve when it loads (rejects on network/parse error). */
function injectScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error(`Failed to load ${src}`));
    };
    document.head.appendChild(script);
  });
}

export function loadTwilioVoice(): Promise<TwilioGlobal> {
  const already = currentGlobal();
  if (already) return Promise.resolve(already);
  if (loadPromise) return loadPromise;

  loadPromise = (async (): Promise<TwilioGlobal> => {
    if (typeof document === 'undefined') {
      throw new Error('Twilio Voice SDK requires a browser environment');
    }
    let lastErr: unknown = null;
    // Try each CDN in order; a 200 body that doesn't define the global (filtering proxy / partial
    // response) is treated like a network failure and falls through to the next URL.
    for (const url of SDK_URLS) {
      try {
        await injectScript(url);
        const g = currentGlobal();
        if (g) return g;
        lastErr = new Error('Twilio Voice SDK loaded but Device is missing');
      } catch (err) {
        lastErr = err;
      }
    }
    loadPromise = null; // every source failed — allow a retry on the next attempt
    throw lastErr instanceof Error ? lastErr : new Error('Failed to load the Twilio Voice SDK');
  })();
  return loadPromise;
}
